/**
 * `x-device-id` request header for the ONE transport (analog of AIM's and
 * SMS's `deviceIdHeader.ts`).
 *
 * The backend binds a session's device identity to the credential: the
 * `x-device-id` on the credential-mint request is minted into a signed
 * `device_id` claim on the access token and carried forward (never
 * re-read) on refresh — so the mint is the one request where web gets to
 * say which device it is. ONE's mints ride the allauth dual transport,
 * NOT `client.ky`: the `/_allauth/*` completion calls (password
 * login, OAuth token exchange, passkey login, signup — via the login
 * adapter's `fetchImpl` config point, `lib/one-auth.ts`) plus the
 * side-flow email-verification mint (`lib/allauth-endpoints.ts`) are all
 * raw `fetch`. Hence `deviceIdFetch` below alongside the ky hook for the
 * `client.ky` escape hatch.
 *
 * Scope: the raw surfaces ONLY — the typed openapi-fetch surface stays
 * unstamped, matching AIM (`main/http/deviceIdHeader.ts`) and
 * SMS (`lib/deviceIdHeader.ts`). ONE has no analog of their
 * `client.DELETE('/api/v2/fcm-token/{id}/')` admin sweep today, but the
 * typed schema is shared across the three apps: keeping ONE's typed
 * surface unstamped means a future typed fcm-token call cannot silently
 * inherit the "deregister my own browser" hazard those files document.
 * Nothing on ONE's typed surface needs the header.
 *
 * WS: ONE connects `api/v2/ws/?token=<jwt>` directly
 * (`lib/socket/socket.ts`) — the JWT itself is the claim carrier, and the
 * backend deliberately ignores a socket's own claim of identity. No
 * header, no restatement.
 */
import type { BeforeRequestState } from 'ky';
import { getPersistedDeviceId } from '../push/deviceId';

export const DEVICE_ID_HEADER = 'x-device-id';

/**
 * ky `beforeRequest` hook (ky v2: hooks receive a single state
 * object) for the `client.ky` escape hatch. Adds the persisted device id,
 * or nothing at all when this browser has none yet.
 */
export function deviceIdBeforeRequest({ request }: BeforeRequestState): void {
  // An explicit per-call header wins: the FCM register/deregister calls
  // (`@aegix/web-push` fcm-api via `push/fcmApi.ts`) name the device they
  // act on, which is not necessarily the one this browser has persisted.
  if (request.headers.has(DEVICE_ID_HEADER)) return;

  const deviceId = getPersistedDeviceId();
  if (deviceId) {
    request.headers.set(DEVICE_ID_HEADER, deviceId);
  }
}

/**
 * `fetch`-compatible wrapper for ONE's raw-fetch surfaces that sit outside
 * `client.ky`: the allauth login adapter (`fetchImpl` config point in
 * `lib/one-auth.ts`) and the allauth side-flow helper
 * (`lib/allauth-endpoints.ts`). Merges the persisted device id into the
 * request headers; an explicit per-call header wins, and a browser with no
 * persisted id sends the request untouched (read-only — the transport
 * never mints an identity).
 */
export const deviceIdFetch: typeof fetch = (input, init) => {
  const deviceId = getPersistedDeviceId();
  if (!deviceId) return fetch(input, init);

  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined),
  );
  if (!headers.has(DEVICE_ID_HEADER)) {
    headers.set(DEVICE_ID_HEADER, deviceId);
  }
  return fetch(input, { ...init, headers });
};
